import type { ReactNode } from 'react';

import {
  AreaTagDeleteLayer,
  type AreaTagDeleteLayerProps,
} from './AreaTagDeleteLayer';
import {
  AreaTagLayer,
  type AreaTagLayerProps,
} from './AreaTagLayer';
import {
  LayoutMarkerLayer,
  type LayoutMarkerLayerProps,
} from './LayoutMarkerLayer';
import {
  MeasurementPolygon,
  type MeasurementPolygonProps,
} from './MeasurementPolygon';
import {
  PolygonEditOverlay,
  type PolygonEditOverlayProps,
} from './PolygonEditOverlay';
import {
  SelectedAreaFocusOverlay,
  type SelectedAreaFocusOverlayProps,
} from './SelectedAreaFocusOverlay';

export interface LayoutSceneProps {
  areaTags?: AreaTagLayerProps;
  measurement?: MeasurementPolygonProps;
  markers?: LayoutMarkerLayerProps;
  polygonEdit?: PolygonEditOverlayProps;
  areaTagDelete?: AreaTagDeleteLayerProps;
  selectedAreaFocus?: SelectedAreaFocusOverlayProps;
  beforeAreaTags?: ReactNode;
  afterAreaTags?: ReactNode;
  beforeMarkers?: ReactNode;
  afterMarkers?: ReactNode;
  children?: ReactNode;
}

/**
 * Canonical ordered scene graph for image and PDF layouts.
 *
 * Hosts provide policy and product-specific presentation through the existing
 * layer resolvers. Heat maps, alerts, and other host decorations use the
 * explicit slots instead of forking the scene order.
 */
export const LayoutScene = ({
  areaTags,
  measurement,
  markers,
  polygonEdit,
  areaTagDelete,
  selectedAreaFocus,
  beforeAreaTags,
  afterAreaTags,
  beforeMarkers,
  afterMarkers,
  children,
}: LayoutSceneProps) => (
  <>
    {beforeAreaTags}
    {areaTags ? <AreaTagLayer {...areaTags} /> : null}
    {afterAreaTags}
    {measurement ? <MeasurementPolygon {...measurement} /> : null}
    {beforeMarkers}
    {markers ? <LayoutMarkerLayer {...markers} /> : null}
    {afterMarkers}
    {selectedAreaFocus ? (
      <SelectedAreaFocusOverlay {...selectedAreaFocus} />
    ) : null}
    {polygonEdit ? <PolygonEditOverlay {...polygonEdit} /> : null}
    {areaTagDelete ? <AreaTagDeleteLayer {...areaTagDelete} /> : null}
    {children}
  </>
);
